import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { REV_RECOVERY_TYPE } from 'src/app/components/admin/location-admin/location-profile-popup/segments/configuration/expanded-sections/revenue-recovery-setting/revenue-recovery-setting.constants';
import {
  LocationGroup,
  LocationProfile,
} from '../../models/admin/location-admin.model';
import {
  FtpScanEvent,
  LocSortScheduleField,
  RevRecoveryCfg,
  SortSchedule,
} from '../../models/admin/location-config.model';

@Injectable({
  providedIn: 'root',
})
export class LocationFormUtil {
  locationItem!: LocationProfile;

  constructor(private fb: FormBuilder) {}

  initForm(locationItem: LocationProfile) {
    this.locationItem = locationItem;
    return this.fb.group({
      locationDetails: this.locationDetailsSection,
      locationGroups: this.locationGroupsSection,
      configurations: this.configurationSection,
    });
  }

  private get locationDetailsSection() {
    return this.fb.group({
      id: [this.locationItem?.locationDetails?.id ?? undefined],
      code: [
        this.locationItem?.locationDetails?.code ?? '',
        Validators.compose([
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(10),
        ]),
      ],
      name: [
        this.locationItem?.locationDetails?.name ?? '',
        Validators.compose([Validators.required, Validators.maxLength(30)]),
      ],
      slic: [
        this.locationItem?.locationDetails?.slic ?? '',
        [Validators.compose([Validators.required, Validators.maxLength(30)])],
      ],
      pkgSlic: [
        this.locationItem?.locationDetails?.pkgSlic ?? '',
        Validators.compose([
          Validators.required,
          Validators.minLength(4),
          Validators.maxLength(5),
        ]),
      ],
      billingPortCode: [
        this.locationItem?.locationDetails?.billingPortCode ?? '',
        Validators.compose([Validators.minLength(4), Validators.maxLength(5)]),
      ],
      region: [
        this.locationItem?.locationDetails?.region ?? '',
        Validators.compose([Validators.required, Validators.maxLength(2)]),
      ],
      district: [
        this.locationItem?.locationDetails?.district ?? '',
        Validators.compose([Validators.required, Validators.maxLength(2)]),
      ],
      opsysId: [
        this.locationItem?.locationDetails?.opsysId ?? '',
        Validators.compose([Validators.required, Validators.maxLength(7)]),
      ],
      cisDest: [
        this.locationItem?.locationDetails?.cisDest ?? '',
        Validators.compose([Validators.required]),
      ],
      country: [this.locationItem?.locationDetails?.country?.isoCode ?? ''],
      primaryEmail: [this.locationItem?.locationDetails?.primaryEmail ?? ''],
      secondaryEmail: [
        this.locationItem?.locationDetails?.secondaryEmail ?? '',
      ],
      timeZone: [
        this.locationItem?.locationDetails?.timeZone ?? '',
        Validators.required,
      ],
    });
  }

  private get locationGroupsSection() {
    if (this.locationItem?.locationGroups?.length > 0) {
      const vals = this.locationItem?.locationGroups?.map(
        (val: LocationGroup) => {
          return {
            id: val.id,
            code: val.code,
            name: val.name,
            siteCount: val.siteCount,
            version: val.version,
          };
        },
      );
      vals.sort((a, b) => {
        return a.code.localeCompare(b.code);
      });
      return this.fb.group({
        assignedGroups: [vals, Validators.required],
      });
    } else {
      return this.fb.group({ assignedGroups: [[], Validators.required] });
    }
  }

  private get configurationSection() {
    return this.fb.group({
      topSection: this.initTopSection,
      gslConfigurations: this.initGslConfigurations,
      customerConfigs: this.initCustomerConfigs,
      equipmentHandling: this.initEquipmentHandling,
      externalSystems: this.initExternalSystems,
    });
  }

  //  CONFIGURATIONS TAB
  private get initTopSection() {
    return this.fb.group({
      customerScanType: [
        this.locationItem?.configurations?.topSection.customerScanType ?? '',
      ],
      customerValidateType: [
        this.locationItem?.configurations?.topSection.customerValidateType ??
          '',
      ],
      customerType: [
        this.locationItem?.configurations?.topSection.customerType ?? 0,
      ],
      dataRetentionDays: [
        this.locationItem?.configurations?.topSection.dataRetentionDays || 4,
        Validators.required,
      ],
      smartDataThreshold: [
        this.locationItem?.configurations?.topSection.smartDataThreshold || 3,
        [Validators.required, Validators.max(99), Validators.min(0)],
      ],
      weekendOpsThreshold: [
        this.locationItem?.configurations?.topSection.weekendOpsThreshold || 5,
        [Validators.required, Validators.max(99), Validators.min(0)],
      ],
      siteType: [
        this.locationItem?.configurations?.topSection.siteType ?? '',
        Validators.required,
      ],
      forceAgentScan: [
        this.locationItem?.configurations?.topSection.forceAgentScan ?? false,
      ],
    });
  }

  private get initGslConfigurations() {
    return this.fb.group({
      positionBasedOps: [
        this.locationItem?.configurations?.gslConfigurations
          ?.positionBasedOps ?? false,
      ],
      enhancedLogging: [
        this.locationItem?.configurations?.gslConfigurations
          ?.enhancedLogging ?? false,
      ],
      isSPSFTrailerTaggingEnabled: [
        this.locationItem?.configurations?.gslConfigurations
          ?.isSPSFTrailerTaggingEnabled ??
          false,
      ],
      blaEnabled: [
        this.locationItem?.configurations?.gslConfigurations?.blaEnabled ??
          false,
      ],
      glsEnabled: [
        this.locationItem?.configurations?.gslConfigurations?.glsEnabled ??
          false,
      ],
      securityScreening: [
        this.locationItem?.configurations?.gslConfigurations
          ?.securityScreening ?? false,
      ],
      inbound: [
        this.locationItem?.configurations?.gslConfigurations?.inbound ?? false,
      ],
      carryOver: [
        this.locationItem?.configurations?.gslConfigurations?.carryOver ??
          false,
      ],
      tslSmartEnabled: [
        this.locationItem?.configurations?.gslConfigurations?.tslSmartEnabled ??
          false,
      ],
      commodityVerification: [
        this.locationItem?.configurations?.gslConfigurations
          ?.commodityVerification ?? false,
      ],
      enhancedInboundScan: [
        this.locationItem?.configurations?.gslConfigurations
          ?.enhancedInboundScan ?? false,
      ],
      bulkPtnAllowed: [
        this.locationItem?.configurations?.gslConfigurations?.bulkPtnAllowed ??
          false,
      ],
      edi: [this.locationItem?.configurations?.gslConfigurations?.edi ?? false],
      manifestVisibility: [
        this.locationItem?.configurations?.gslConfigurations
          ?.manifestVisibility ?? false,
      ],
      allowOpsysWebCall: [
        this.locationItem?.configurations?.gslConfigurations
          ?.allowOpsysWebCall ?? false,
      ],
      deleteUld: [
        this.locationItem?.configurations?.gslConfigurations?.deleteUld ??
          false,
      ],
      ftpScanEvents: [
        this.locationItem?.configurations?.gslConfigurations?.ftpScanEvents ??
          false,
      ],
      revRecovery: [
        this.locationItem?.configurations?.gslConfigurations?.revRecovery ??
          false,
      ],
      sortSchedule: [
        this.locationItem?.configurations?.gslConfigurations?.sortSchedule ??
          false,
      ],
      ediDetails: this.ediDetails(),
      ftpEvents: this.initFtpScanRows(
        this.locationItem?.configurations?.gslConfigurations?.ftpEvents,
      ),
      revenueRecoveryDetails: this.fb.group({
        revRecoveryType: [REV_RECOVERY_TYPE.PACKAGE],
        revRecoveryPackage: this.fb.group(
          this.getRevRecoveryPackageDetails(
            this.locationItem?.configurations?.gslConfigurations
              ?.revenueRecoveryDetails?.revRecoveryPackage,
          ),
        ),
        revRecoveryPallet: this.fb.group(
          this.getRevRecoveryPalletDetails(
            this.locationItem?.configurations?.gslConfigurations
              ?.revenueRecoveryDetails?.revRecoveryPallet,
          ),
        ),
      }),
      sortScheduleDetails: this.fb.array([
        this.getSortSchedule(
          this.locationItem?.configurations?.gslConfigurations
            ?.sortScheduleDetails[0],
          'Sunrise',
          'S1',
          0,
          '7:00',
          '9:59',
        ),
        this.getSortSchedule(
          this.locationItem?.configurations?.gslConfigurations
            ?.sortScheduleDetails[1],
          'Morning',
          'M1',
          1,
          '10:00',
          '10:59',
        ),
        this.getSortSchedule(
          this.locationItem?.configurations?.gslConfigurations
            ?.sortScheduleDetails[2],
          'Day',
          'D1',
          2,
          '11:00',
          '14:59',
        ),
        this.getSortSchedule(
          this.locationItem?.configurations?.gslConfigurations
            ?.sortScheduleDetails[3],
          'Twilight',
          'T1',
          3,
          '18:00',
          '20:59',
        ),
        this.getSortSchedule(
          this.locationItem?.configurations?.gslConfigurations
            ?.sortScheduleDetails[4],
          'Night',
          'N1',
          4,
          '21:00',
          '23:59',
        ),
        this.getSortSchedule(
          this.locationItem?.configurations?.gslConfigurations
            ?.sortScheduleDetails[5],
          'Preload',
          'P1',
          5,
          '0:00',
          '6:59',
        ),
        this.getSortSchedule(
          this.locationItem?.configurations?.gslConfigurations
            ?.sortScheduleDetails[6],
          'Local',
          'L1',
          6,
          '16:00',
          '17:59',
        ),
        this.getSortSchedule(
          this.locationItem?.configurations?.gslConfigurations
            ?.sortScheduleDetails[7],
          'Afternoon',
          'A1',
          7,
          '15:00',
          '15:59',
        ),
      ]),
    });
  }

  private get initCustomerConfigs() {
    return this.fb.group({
      cvis: [this.locationItem?.configurations?.customerConfigs?.cvis],
      customerLocationApi: [
        this.locationItem?.configurations?.customerConfigs
          ?.customerLocationApi ?? false,
      ],
      customer: [this.locationItem?.configurations?.customerConfigs?.customer],
      cvisSmartEnabled: [
        this.locationItem?.configurations?.customerConfigs?.cvisSmartEnabled ??
          false,
      ],
      customerCounter: [
        this.locationItem?.configurations?.customerConfigs?.customerCounter ??
          false,
      ],
      cvisSmartDetails: this.fb.group({
        cvisHardware: [
          this.locationItem?.configurations?.customerConfigs?.cvisSmartDetails
            ?.cvisHardware ?? false,
        ],
        cvisIterations: [
          this.locationItem?.configurations?.customerConfigs?.cvisSmartDetails
            ?.cvisIterations ?? 1,
        ],
      }),
      customerLocationDetails: this.initCustomerLocDetails,
    });
  }

  private get initEquipmentHandling() {
    return this.fb.group({
      ahcs: [
        this.locationItem?.configurations?.equipmentHandling?.ahcs ?? false,
      ],
      tfcs: [
        this.locationItem?.configurations?.equipmentHandling?.tfcs ?? false,
      ],
      loadHandling: [
        this.locationItem?.configurations?.equipmentHandling?.loadHandling ??
          false,
      ],
      alp: [this.locationItem?.configurations?.equipmentHandling?.alp ?? false],
    });
  }

  private get initExternalSystems() {
    return this.fb.group({
      messagingActive: [
        this.locationItem?.configurations?.externalSystems?.messagingActive ??
          false,
      ],
      intercepts: [
        this.locationItem?.configurations?.externalSystems?.intercepts ?? false,
      ],
      gioEnabled: [
        this.locationItem?.configurations?.externalSystems?.gioEnabled ?? false,
      ],
      opsys: [
        this.locationItem?.configurations?.externalSystems?.opsys ?? false,
      ],
      hmmsEnabled: [
        this.locationItem?.configurations?.externalSystems?.hmmsEnabled ??
          false,
      ],
      rcc: [this.locationItem?.configurations?.externalSystems?.rcc ?? false],
      collectShippers: [
        this.locationItem?.configurations?.externalSystems?.collectShippers ??
          false,
      ],
    });
  }

  private get initCustomerLocDetails() {
    return this.fb.array([
      this.getCustomerDetails('01', 0),
      this.getCustomerDetails('02', 1),
      this.getCustomerDetails('03', 2),
      this.getCustomerDetails('04', 3),
      this.getCustomerDetails('05', 4),
      this.getCustomerDetails('06', 5),
      this.getCustomerDetails('07', 6),
      this.getCustomerDetails('08', 7),
    ]);
  }

  private getCustomerDetails(sortCode: string, index: number) {
    const data = this.locationItem?.configurations?.customerConfigs
      ?.customerLocationDetails
      ? this.locationItem?.configurations?.customerConfigs
          ?.customerLocationDetails[index]
      : null;

    return this.fb.group({
      id: [data?.id ?? null],
      sort: [sortCode],
      sun: [data?.sun ?? 0],
      mon: [data?.mon ?? 0],
      tue: [data?.tue ?? 0],
      wed: [data?.wed ?? 0],
      thr: [data?.thr ?? 0],
      fri: [data?.fri ?? 0],
      sat: [data?.sat ?? 0],
    });
  }

  private getSortSchedule(
    val: SortSchedule,
    sortType: string,
    sortCode: string,
    index: number,
    defaultStartTime: string,
    defaultEndTime: string,
  ) {
    return this.fb.group({
      sortType: [sortType],
      sortCode: [sortCode],
      idx: [index],
      sun: this.getScheduleField(val?.sun, defaultStartTime, defaultEndTime),
      mon: this.getScheduleField(val?.sat, defaultStartTime, defaultEndTime),
      tue: this.getScheduleField(val?.tue, defaultStartTime, defaultEndTime),
      wed: this.getScheduleField(val?.wed, defaultStartTime, defaultEndTime),
      thr: this.getScheduleField(val?.thr, defaultStartTime, defaultEndTime),
      fri: this.getScheduleField(val?.sat, defaultStartTime, defaultEndTime),
      sat: this.getScheduleField(val?.sun, defaultStartTime, defaultEndTime),
    });
  }

  private getScheduleField(
    val: LocSortScheduleField,
    defaultStartTime: string,
    defaultEndTime: string,
  ) {
    return this.fb.group({
      startTime: [val?.startTime ?? defaultStartTime],
      endTime: [val?.endTime ?? defaultEndTime],
      offset: [val?.offset ?? 0],
    });
  }

  private ediDetails(): FormGroup {
    return this.fb.group({
      ediDelay: [
        this.locationItem?.configurations?.gslConfigurations?.ediDetails
          ?.ediDelay ?? false,
      ],
      ediDelayInterval: [
        this.locationItem?.configurations?.gslConfigurations?.ediDetails
          ?.ediDelayInterval ?? 15,
      ],
    });
  }

  private getRevRecoveryPackageDetails(val: RevRecoveryCfg) {
    if (this.locationItem?.locationDetails?.country?.isoCode === 'I') {
      return {
        //Update on post from form
        origSlic: [val?.origSlic],
        origCountry: [val?.origCountry],
        locCode: [val?.locCode],
        //
        id: [val?.id],
        locationId: [val?.locationId],
        destSlic: [val?.destSlic],
        destCountry: [val?.destCountry],
        untOfMeasureSystem: ['I'],
        uintOfMeasureDimCode: ['001'],
        uintOfMeasureWgtCode: ['039'],
        uowTrkNumberType: ['97'],
        maxDimension: [val?.maxDimension ?? '108.0', Validators.min(0.01)],
        minDimension: [val?.minDimension ?? '0.4', Validators.min(0.01)],
        maxGirthDimension: [
          val?.maxGirthDimension ?? '165.0',
          Validators.min(0.01),
        ],
        minGirthDimension: [
          val?.minGirthDimension ?? '1.0',
          Validators.min(0.01),
        ],
        maxWeight: [val?.maxWeight ?? '150.0', Validators.min(0.01)],
        minWeight: [val?.minWeight ?? '0.5', Validators.min(0.01)],
      };
    } else {
      return {
        //Update on post from form
        origSlic: [val?.origSlic],
        origCountry: [val?.origCountry],
        locCode: [val?.locCode],
        //
        id: [val?.id],
        locationId: [val?.locationId],
        destSlic: [val?.destSlic],
        destCountry: [val?.destCountry],
        untOfMeasureSystem: ['M'],
        uintOfMeasureDimCode: ['002'],
        uintOfMeasureWgtCode: ['043'],
        uowTrkNumberType: ['97'],
        maxDimension: [val?.maxDimension ?? '274.3', Validators.min(0.01)],
        minDimension: [val?.minDimension ?? '1.0', Validators.min(0.01)],
        maxGirthDimension: [
          val?.maxGirthDimension ?? '300.0',
          Validators.min(0.01),
        ],
        minGirthDimension: [
          val?.minGirthDimension ?? '2.54',
          Validators.min(0.01),
        ],
        maxWeight: [val?.maxWeight ?? '68.2', Validators.min(0.01)],
        minWeight: [val?.minWeight ?? '0.5', Validators.min(0.01)],
      };
    }
  }

  private getRevRecoveryPalletDetails(val: RevRecoveryCfg) {
    if (this.locationItem?.locationDetails?.country?.isoCode === 'I') {
      return {
        //Update on post from form
        origSlic: [val?.origSlic],
        origCountry: [val?.origCountry],
        locCode: [val?.locCode],
        //
        id: [val?.id],
        locationId: [val?.locationId],
        destSlic: [val?.destSlic],
        destCountry: [val?.destCountry],
        untOfMeasureSystem: ['I'],
        uintOfMeasureDimCode: ['001'],
        uintOfMeasureWgtCode: ['039'],
        uowTrkNumberType: ['12'],
        maxDimension: [val?.maxDimension ?? '999.0'],
        minDimension: [val?.minDimension ?? '1.0'],
        maxWeight: [val?.maxWeight ?? '9999.9'],
        minWeight: [val?.minWeight ?? '0.5'],
      };
    } else {
      return {
        //Update on post from form
        origSlic: [val?.origSlic],
        origCountry: [val?.origCountry],
        locCode: [val?.locCode],
        //
        id: [val?.id],
        locationId: [val?.locationId],
        destSlic: [val?.destSlic],
        destCountry: [val?.destCountry],
        untOfMeasureSystem: ['M'],
        uintOfMeasureDimCode: ['002'],
        uintOfMeasureWgtCode: ['043'],
        uowTrkNumberType: ['12'],
        maxDimension: [val?.maxDimension ?? '999.0'],
        minDimension: [val?.minDimension ?? '2.0'],
        maxWeight: [val?.maxWeight ?? '9999.9'],
        minWeight: [val?.minWeight ?? '0.5'],
      };
    }
  }

  private initFtpScanRows(values: FtpScanEvent[]) {
    if (values?.length > 0) {
      return this.fb.array(
        values.map((val) => {
          return this.fb.group({
            id: val.id,
            locationId: val.locationId,
            isftps: val.isftps ?? false,
            isSecureftp: [val?.isSecureftp ?? false],
            hostName: [val?.hostName ?? ''],
            directory: [val?.directory ?? ''],
            userName: [val?.userName ?? ''],
            password: [val?.password ?? ''],
            sftpPortnum: [val?.sftpPortnum ?? 0],
          });
        }),
      );
    } else {
      return this.fb.array([
        this.fb.group({
          id: 0,
          locationId: 0,
          isftps: false,
          isSecureftp: [false],
          hostName: [''],
          directory: [''],
          userName: [''],
          password: [''],
          sftpPortnum: [''],
        }),
      ]);
    }
  }

  /////////////////////////////////////
}
